import { JandiLogo } from "@/components/assets/logos/svgs";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// import HeaderNavContent from "@/header/HeaderNavContent";
import { logoutUser } from "@/features/user/userSlice";
import { RootState } from "@/store/store";
import Popup from "./popups/popupDetails";
import Login from "@/pages/auth/login/index";
import Register from "@/pages/auth/register";
import ForgotPassword from "@/pages/auth/forgotPassword";
import VerifyAccount from "@/pages/auth/verify-account";
import ResetPassword from "@/pages/auth/resetPassword";
import { isAuthenticated, Logout } from "./services/credentialsService";
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AvatarDropdown from './AvatarDropdown';
import SearchService from "@/components/header/SearchService";
import MobileHeader, { MOBILE_HEADER_BAR_HEIGHT_PX } from "./MobileHeader";
import { toast } from "@/utils/appToast";
import { api } from "@/utils/api";
import type { WorkerProfile } from "@/types/userTypes";
import { removeFcmTokenOnLogout } from "@/utils/pushUtils";
import { Spinner } from "./spinner/spinner";
import { useTranslation } from 'react-i18next';
import LanguageDropdown from "@/components/selectLanguage";
import { PATHS } from "@/shared/publicPaths";

// Detect mobile chrome. Always start `true` so SSR + first client paint match
// (avoids hydration mismatch) and iPhone in-app browsers never land on the
// desktop header whose `.main-box` is `display:none` under 1023px.
function useIsMobile(breakpoint = 1266) {
  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const update = () => setIsMobile(mq.matches);
    update(); // must run on mount — resize alone never fires in Instagram WKWebView
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, [breakpoint]);

  return isMobile;
}

const sidebarStyles: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  height: '100vh',
  width: '100vw',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  zIndex: 2000,
  transform: 'translateX(0)',
  transition: 'transform 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  paddingTop: 'env(safe-area-inset-top, 0px)',
  boxShadow: '0 0 40px rgba(0, 0, 0, 0.1)',
};

const overlayStyles: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(0,0,0,0.5)',
  zIndex: 1999,
  backdropFilter: 'blur(4px)',
};

const closeIconStyles: React.CSSProperties = {
  position: 'absolute',
  top: 24,
  right: 24,
  fontSize: 28,
  cursor: 'pointer',
  zIndex: 2100,
  color: '#64748b',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease',
};

const menuItemStyle: React.CSSProperties = {
  fontFamily: '"Object Sans", system-ui, sans-serif',
  fontSize: 15,
  fontWeight: 500,
  color: "#1e293b",
  height: "56px",
  lineHeight: "56px",
  margin: 0,
  padding: '0 32px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '16px',
};

const logoContainerStyle: React.CSSProperties = {
  padding: '32px 32px 24px 32px',
  borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
  background: 'linear-gradient(135deg, #4285F4 0%, #1967D2 100%)',
  marginBottom: '16px',
};

const menuContainerStyle: React.CSSProperties = {
  padding: '0',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const menuItemHoverStyle: React.CSSProperties = {
  background: 'rgba(59, 130, 246, 0.05)',
  color: '#4285F4',
  transform: 'translateX(4px)',
};


const Header = () => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const [ready, setReady] = useState(() => i18n.isInitialized);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [navbar, setNavbar] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [forcePasswordReset, setForcePasswordReset] = useState(false)
  const[resetUserId, setResetUserId] = useState('');
  const [loginPopupShow, setLoginPopupToggle] = useState(false);
  const [forgotPassPopupShow, setForgotPassPopupToggle] = useState(false);
  const [resetPasswordPopupShow, setResetPasswordPopupShow] = useState(false);
  const [registerPopupShow, setRegisterPopupToggle] = useState(false);
  const [registerType, setRegisterType] = useState("");
  const [verifyAccountPopupShow, setVerifyAccountPopupToggle] = useState(false)
  const { user, token } = useSelector((state: RootState) => state.user);
  const isWorker = user.type === "worker";
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [workerProfileData, setWorkerProfileData] = useState<WorkerProfile>();

  useEffect(() => {
    const pathname = location.pathname;

    if (pathname === "/verify-account") {
      const code = searchParams.get("code");
      const email = searchParams.get("email");
      if (code && email) {
        setVerifyAccountPopupToggle(true);
      }
    }

    if (pathname === "/reset-password") {
      const token = searchParams.get("token");
      const email = searchParams.get("email");
      if (token && email) {
        setResetPasswordPopupShow(true); // or a new state like `setResetPasswordPopupToggle(true)`
      }
    }

    // Check for registerType query param to open register popup
    const registerType = searchParams.get("registerType");
    if (registerType && (registerType === "user" || registerType === "worker")) {
      setRegisterType(registerType);
      setRegisterPopupToggle(true);
      // Remove the query param from URL
      navigate(pathname, { replace: true });
    }

    // Check for openLogin query param to navigate to login page
    const openLogin = searchParams.get("openLogin");
    if (openLogin === "true") {
      navigate(PATHS.login, { replace: true });
    }

    // Check for forgotPassword query param to navigate to forgot password page
    const forgotPassword = searchParams.get("forgotPassword");
    if (forgotPassword === "true") {
      navigate("/forgot-password", { replace: true });
    }
  }, [location, searchParams, navigate]);

  useEffect(() => {
    if (i18n.isInitialized) {
      setReady(true);
    } else {
      const handleInit = () => setReady(true);
      i18n.on("initialized", handleInit);
      return () => {
        i18n.off("initialized", handleInit);
      };
    }
  }, [i18n]);

  

  const changeBackground = () => {
    if (window.scrollY >= 10) {
      setNavbar(true);
    } else {
      setNavbar(false);
    }

  };

  const onGoToLogin = (type) => {
    handlePopupType(type)
  }

  const handleLoginPopup = async () => {
    console.log("@@@")
    // deleteBlog(feeDetail);
  }

  const handleRegisterPopup = async () => {
    console.log("@@@")
    // deleteBlog(feeDetail);
  }

  const handlePopupType = (type: string) => {
    switch (type) {
      case 'login':
        {
          setRegisterPopupToggle(false);
          navigate(PATHS.login);
          setForgotPassPopupToggle(false);
          setResetPasswordPopupShow(false);
          setVerifyAccountPopupToggle(false);
          break
        }

      case 'Register':
        {
          setLoginPopupToggle(false);
          setRegisterPopupToggle(true);
          break
        }

      case 'ForgotPassword':
        {
          setLoginPopupToggle(false);
          setRegisterPopupToggle(false);
          navigate("/forgot-password");
          break
        }
      case 'VerifyAccount':
        {
          setLoginPopupToggle(false);
          setRegisterPopupToggle(false);
          setForgotPassPopupToggle(false);
          setVerifyAccountPopupToggle(true);
          break;
        }

      case 'Close':
        {
          setLoginPopupToggle(false);
          setRegisterPopupToggle(false);
          setForgotPassPopupToggle(false);
          break
        }
        case 'ResetPassword':
        {
           setForcePasswordReset(true)
           setLoginPopupToggle(false);               // close login popup
          setResetPasswordPopupShow(true); 
          break
        }

      default:
        break;

    }
  }



  const resetPasswordClose = () => {
    setResetPasswordPopupShow(false)
    navigate('/')
  }

  const verifyPasswordClose = () => {
    setVerifyAccountPopupToggle(false)
    navigate('/')
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleOpenRegister = (type) => {
    setRegisterType(type); // 1. Set the type
    setRegisterPopupToggle(true); // 2. Then open popup
  };


  const handleLogout = () => {
    dispatch(logoutUser());
    Logout();
    handleProfileMenuClose();
    toast.success(t("toastLogoutSuccess"));
    navigate('/');
  };
  const handleDashboard = () => {
    handleProfileMenuClose();
    if (user?.type === 'worker') {
      navigate('/my-jobs');
    } else {
      navigate('/my-proposals');
    }
  };
  const handleSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  const settingUserId = (id: string) =>{
    console.log("hello ji")
    setResetUserId(id)
  } 

  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Handler for opening registration popup from sidebar
  const handleSidebarRegister = (type: string) => {
    setRegisterType(type); // 1. Set the type
    setRegisterPopupToggle(true); // 2. Then open popup
    setSidebarOpen(false);
  };


  // const handleOpenRegister = (type) => {
  //   setRegisterType(type); // 1. Set the type
  //   setRegisterPopupToggle(true); // 2. Then open popup
  // };

  // Handler for opening login page from sidebar
  const handleSidebarLogin = () => {
    navigate(PATHS.login);
    setSidebarOpen(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", changeBackground);
  }, []);

  const handleSidebarDashboard = () => {
    handleProfileMenuClose();
    if (user?.type === 'worker') {
      navigate('/my-jobs');
    } else {
      // TODO: Add client dashboard route here
      navigate('/my-proposals');
    }
  };
  const handleSidebarSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  // Fetch worker profile for Stripe status on mobile menu
  useEffect(() => {
    const fetchWorkerProfile = async () => {
      try {
        if (user?.type === 'worker') {
          const wp = await api.getMyWorkerProfile();
          if (wp) setWorkerProfileData(wp);
        }
      } catch (e) {
        // ignore
      }
    };
    fetchWorkerProfile();
  }, [user?.type]);

  const handleSidebarStripe = async () => {
    if (user?.type !== 'worker') return;
    try {
      setStripeLoading(true);
      
      // Open a temporary blank tab synchronously — this keeps iOS happy
      const newWindow = window.open("", "_blank");
      
      // If popup was blocked, redirect in same tab
      if (!newWindow) {
        if (workerProfileData?.isStripeOnBoardingCompleted) {
          const data = await api.getStripeDashboardLink();
          if (data?.url) window.location.href = data.url;
        } else {
          const { status, data } = await api.getAccountLink();
          if (status !== 'error') {
            const onboardingLink = data?.data;
            if (onboardingLink) window.location.href = onboardingLink;
          }
        }
        setSidebarOpen(false);
        return;
      }
      
      if (workerProfileData?.isStripeOnBoardingCompleted) {
        const data = await api.getStripeDashboardLink();
        if (data?.url) {
          newWindow.location.href = data.url;
        } else {
          newWindow.close();
        }
      } else {
        const { status, data } = await api.getAccountLink();
        if (status !== 'error') {
          const onboardingLink = data?.data;
          if (onboardingLink) {
            newWindow.location.href = onboardingLink;
          } else {
            newWindow.close();
          }
        } else {
          newWindow.close();
        }
      }
      
      setSidebarOpen(false);
    } catch (e) {
      // silent fail
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSidebarMessages = () => {
    handleProfileMenuClose();
    navigate('/chat');
  };

  const handleLogoutClick = async () => {
    setIsLoading(true);
    await removeFcmTokenOnLogout()
    handleLogout();
    handleProfileMenuClose();
    setIsLoading(false);
  };

  // Show chrome even before i18n finishes so iOS/Instagram never get a blank top bar
  if (!ready) {
    if (isMobile) {
      return (
        <>
          <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
          {/* Body already applies safe-area inset; spacer only clears the fixed toolbar */}
          <div
            aria-hidden
            style={{
              height: MOBILE_HEADER_BAR_HEIGHT_PX,
              width: "100%",
              flexShrink: 0,
            }}
          />
        </>
      );
    }
    return null;
  }

  if (isMobile) {
    return (
      <>
        {/* Only show MobileHeader when sidebar is closed */}
        {!sidebarOpen && <MobileHeader onMenuClick={() => setSidebarOpen(true)} />}
        {/* Body already applies safe-area inset; spacer only clears the fixed toolbar */}
        <div
          aria-hidden
          style={{
            height: MOBILE_HEADER_BAR_HEIGHT_PX,
            width: "100%",
            flexShrink: 0,
          }}
        />
        {sidebarOpen && <div style={overlayStyles} onClick={() => setSidebarOpen(false)} />}
        {sidebarOpen && (
          <div style={sidebarStyles}>
            {/* Close icon */}
            <span style={closeIconStyles} onClick={() => setSidebarOpen(false)} aria-label="Close sidebar">&times;</span>
            {/* Logo and header */}
            <div style={logoContainerStyle}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 12 }}>
                <JandiLogo style={{ height: 40, filter: 'brightness(0) invert(1)' }} />
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 12, padding: '2px 6px' }}>
                  <LanguageDropdown />
                </div>
              </div>
            </div>
            {/* Menu Container */}
            <div style={menuContainerStyle}>
              {!isAuthenticated() && (
                <div style={{ ...menuItemStyle, ...menuItemHoverStyle }} onClick={handleSidebarLogin}>
                  <span style={{ fontSize: '20px' }}>👤</span>
                  {t('login')}
                </div>
              )}

              {isAuthenticated() && (
                <>
                  <div style={{ ...menuItemStyle, ...menuItemHoverStyle }} onClick={handleSidebarDashboard}>
                    <span style={{ fontSize: '20px' }}>📊</span>
                    {user?.type === 'worker' ? 'My jobs' : t('myProposals')}
                  </div>
                <div
  style={{
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    ...menuItemStyle,
    ...menuItemHoverStyle,
  }}
  onClick={handleSidebarMessages}
>
  <span style={{ position: "relative", fontSize: "20px", marginRight: "8px" }}>
    💬
    {user?.unSeenMessageCounter > 0 && (
      <span
        style={{
          position: "absolute",
          top: "-2px",
          right: "-6px",
          background: "red",
          color: "white",
          borderRadius: "50%",
          padding: "1px 4px",
          fontSize: "10px",
          fontWeight: "bold",
          lineHeight: 1,
        }}
      >
        {user?.unSeenMessageCounter}
      </span>
    )}
  </span>
  {t("messages")}
</div>

                  <div style={{ ...menuItemStyle, ...menuItemHoverStyle }} onClick={handleSidebarSettings}>
                    <span style={{ fontSize: '20px' }}>⚙️</span>
                    {t('settings')}
                  </div>
                  {user?.type === 'worker' &&
                    (workerProfileData?.paymentType ?? user?.paymentType) !== 'ATH' && (
                    <div
                      style={{ ...menuItemStyle, ...menuItemHoverStyle, opacity: stripeLoading ? 0.7 : 1, pointerEvents: stripeLoading ? 'none' : 'auto' }}
                      onClick={handleSidebarStripe}
                    >
                      <span style={{ fontSize: '20px' }}>💳</span>
                      {t(workerProfileData?.isStripeOnBoardingCompleted ? 'stripeDashboard' : 'stripeOnbaord')}
                    </div>
                  )}
                  <div style={{ ...menuItemStyle, ...menuItemHoverStyle }} onClick={handleLogoutClick}>
                    <span style={{ fontSize: '20px' }}>🚪</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Spinner size={'xsmall'} isLoading={isLoading} />
                      {t('logout')}
                    </div>
                  </div>
                </>
              )}

              {pathname !== "/update-worker"  && !isAuthenticated() && (
                <>
                  <div style={{ ...menuItemStyle, ...menuItemHoverStyle }} onClick={() => {
                    navigate(PATHS.signup);
                    setSidebarOpen(false);
                  }}>
                    <span style={{ fontSize: '20px' }}>📝</span>
                    {t('signup')}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {/* Registration Popup (keep your existing popup logic) */}
        <Popup
          size={'sm'}
          className="bg-darkBlue"
          headerTitle=""
          isOpen={registerPopupShow}
          handleSubmit={handleRegisterPopup}
          handleClose={() => setRegisterPopupToggle(false)}
          bodyChildren={<Register handlePopupType={handlePopupType} RegisterType={registerType} />}
          slotProps={{
            root: { sx: { zIndex: 4000 } }
          }}
        />

        <Popup
          size={'sm'}
          className="bg-darkBlue"
          headerTitle=""
          isOpen={loginPopupShow}
          handleSubmit={handleLoginPopup}
          handleClose={() => setLoginPopupToggle(false)}
          bodyChildren={
            <Login handlePopupType={handlePopupType}  setUserId={(id: string) => {
    setResetUserId(id)
  }} />
          }
        />

        <Popup
          size="sm"
          className="bg-darkBlue"
          headerTitle=""
          isOpen={verifyAccountPopupShow}
          handleClose={() => verifyPasswordClose()}
          bodyChildren={
            <VerifyAccount handlePopupType={handlePopupType} />
          }
        />

        <Popup
          size="sm"
          className="bg-darkBlue"
          headerTitle=""
          isOpen={resetPasswordPopupShow}
          handleClose={() => resetPasswordClose()}
          bodyChildren={
            <ResetPassword
              token={searchParams.get("token") || ""}
              email={searchParams.get("email") || ""}
              onHide={() => setResetPasswordPopupShow(false)} onGoToLogin={onGoToLogin}
               forcePasswordReset={forcePasswordReset} resetUserId={resetUserId} 
               />
          }
        />

        <Popup
          size={'sm'}
          className="bg-darkBlue"
          headerTitle=""
          isOpen={forgotPassPopupShow}
          // handleSubmit={handleForgotPopup}
          handleClose={() => setForgotPassPopupToggle(false)}
          bodyChildren={
            <ForgotPassword handlePopupType={handlePopupType} />
          }
        />


      </>
    );
  }


  return (
    // <!-- Main Header-->
    <header
      className={`bg-home main-header header-style-four -type-16 shadow-sm ${navbar ? "fixed-header animated slideInDown" : ""}`}
      style={{
        backgroundColor: pathname.includes(PATHS.services) ? "#fff" : "",
      }}
    >
      <Popup
        size={'sm'}
        className="bg-darkBlue"
        headerTitle=""
        isOpen={loginPopupShow}
        handleSubmit={handleLoginPopup}
        handleClose={() => setLoginPopupToggle(false)}
        bodyChildren={
           <Login handlePopupType={handlePopupType}  setUserId={(id: string) => {
   
    setResetUserId(id)
  }} />
        }
      />

      <Popup
        size="sm"
        className="bg-darkBlue"
        headerTitle=""
        isOpen={verifyAccountPopupShow}
        handleClose={() => verifyPasswordClose()}
        bodyChildren={
          <VerifyAccount handlePopupType={handlePopupType} />
        }
      />


      <Popup
        size={'sm'}
        className="bg-darkBlue"
        headerTitle=""
        isOpen={registerPopupShow}
        handleSubmit={handleRegisterPopup}
        handleClose={() => setRegisterPopupToggle(false)}
        bodyChildren={
          <Register handlePopupType={handlePopupType} RegisterType={registerType} />
        }
      />
      <Popup
        size="sm"
        className="bg-darkBlue"
        headerTitle=""
        isOpen={resetPasswordPopupShow}
        handleClose={() => resetPasswordClose()}
        bodyChildren={
          <ResetPassword
            token={searchParams.get("token") || ""}
            email={searchParams.get("email") || ""}
            onHide={() => setResetPasswordPopupShow(false)} onGoToLogin={onGoToLogin} 
              forcePasswordReset={forcePasswordReset} resetUserId={resetUserId} 
              />
        }
      />

      <Popup
        size={'sm'}
        className="bg-darkBlue"
        headerTitle=""
        isOpen={forgotPassPopupShow}
        // handleSubmit={handleForgotPopup}
        handleClose={() => setForgotPassPopupToggle(false)}
        bodyChildren={
          <ForgotPassword handlePopupType={handlePopupType} />
        }
      />



      <div className="container-fluid">
        <div className="main-box">
          {/* <!--Nav Outer --> */}
          <div className="nav-outer  ">
            <div className="">
              <div className="logo me-4 -ml-4">
                <Link to="/">
                  <JandiLogo className="w-75 h-75" />
                </Link>
              </div>
            </div>
            {/* End .logo-box */}

          {/* language section start */}
          <LanguageDropdown/>
          {/* language section end */}

            {/* <nav className="nav main-menu">
              <ul className="navigation" id="navbar">
                {isWorker && token && (
                  <li style={{ marginRight: 40 }}>
                    <Link to="/dashboard" className="theme-btn btn-style-nine">
                      Panel del profesional
                    </Link>
                  </li>
                )}
                {!isWorker && token && (
                  <li>
                    <Link to="/dashboard" className="theme-btn btn-style-nine">
                      Dashboard
                    </Link>
                    
                  </li>
                )}
                {pathname !== "/" && !pathname.includes("/candidates-dashboard") && (
            <SearchService />
          )}
              </ul>
            </nav> */}

            {/* <!-- Main Menu End--> */}
          </div>
          {/* End .nav-outer */}

          <div className="outer-box  " style={{ position: "relative", left: "90px" }}>
            {/* <!-- Login/Register or Profile Dropdown --> */}
           {/* Right-side button area */}
{/* Right-side button area */}
<div className="outer-box">
  <div
    className="container-fluid flex justify-end items-center"
    style={{  }} // controls distance from right edge
  >
    <div className="btn-box flex items-center gap-1">
      {!isAuthenticated() && pathname !== "/update-worker" ? (
        <>
          <Link
            to={PATHS.signup}
            className="theme-btn btn-style-nine call-modal"
          >
            {t("signup")}
          </Link>
          <Link
            to={PATHS.login}
            className="theme-btn btn-style-nine call-modal"
          >
            {t("login")}
          </Link>
        </>
      ) : (
        pathname !== "/update-worker" &&
        isAuthenticated() && (
          <AvatarDropdown
            user={user}
            onLogout={() => {
              dispatch(logoutUser());
              Logout();
              navigate("/");
            }}
          />
        )
      )}
    </div>
  </div>
</div>

          </div>
        </div>
        {/* <!-- Main box --> */}
      </div>
    </header>
  );
};

export default Header;
